import React, { useRef, useMemo } from 'react'
import { Message } from 'rsuite'
import JoditEditor from 'jodit-react'
import useStandardToaster from '../../ui_reusable/StandardToaster'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import UniversalLoader from '../../ui_reusable/UniversalLoader'
import { PanelSpecialIndent } from '../../ui_reusable/styles/PlacementMisc.styles'
import { StandardButton } from '../../ui_reusable/styles/ButtonStyles'
import {
  useLearningsRetrieve,
  useLearningsPartialUpdate,
} from '../../../api/generated/endpoints/learning/learning'
import type { PatchedLearningRequest } from '../../../api/generated/models'
import {
  learningsPartialUpdateBody,
  learningsRetrieveResponse,
} from '../../../api/generated/validation/learning/learning.zod'
// Import our enhanced validation utility
import { validateAndLog, validateForRequest } from '../../../utils/zodDebugger'

const ManageIntroLearningPage: React.FC = () => {
  const editor = useRef<any>(null)
  const { pushToast } = useStandardToaster()

  // Use the orval-generated query hook
  const {
    data: learningData,
    isLoading,
    error,
  } = useLearningsRetrieve({
    query: {
      staleTime: 0,
      gcTime: 0,
    },
  })

  // Runtime validation with enhanced debugging (one-liner)
  const validatedData = validateAndLog(
    learningsRetrieveResponse,
    learningData,
    'ManageIntroLearningPage',
  )

  // Use the orval-generated mutation hook
  const updateLearningMutation = useLearningsPartialUpdate({
    mutation: {
      onSuccess: () => {
        pushToast(<Message type="success">Intro saved successfully.</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
      },
      onError: () => {
        pushToast(<Message type="error">Failed to save content.</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
      },
    },
  })

  const saveContent = async (): Promise<void> => {
    // Get current content directly from the editor
    const currentContent = editor.current?.value || ''

    // Basic validation for empty content
    if (!currentContent.trim()) {
      pushToast(
        <Message type="warning">Intro content cannot be empty.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }

    const updateData: PatchedLearningRequest = {
      intro: currentContent.trim(),
    }

    // Validate data with enhanced debugging (for development/debugging only)
    try {
      const validData = validateForRequest(
        learningsPartialUpdateBody,
        updateData,
        'SaveIntro',
      )
      updateLearningMutation.mutate({  data: validData })
    } catch (error) {
      // Error already logged to console with detailed debugging
      pushToast(
        <Message type="error">Please check the data and try again.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      return
    }
  }

  const config = useMemo(
    () => ({
      readonly: false,
      placeholder: 'Start typing...',
      height: '80vh',
      uploader: {
        insertImageAsBase64URI: true,
      },
      buttons: [
        'bold',
        'strikethrough',
        'underline',
        'italic',
        '|',
        'ul',
        'ol',
        '|',
        'outdent',
        'indent',
        '|',
        'font',
        'fontsize',
        'paragraph',
        '|',
        'image',
        'link',
        '|',
        'align',
        'undo',
        'redo',
        '|',
        'hr',
        'eraser',
        'fullsize',
      ],
    }),
    [],
  )

  if (error) {
    return <div>Error loading intro content</div>
  }

  return (
    <>
      <SimpleAccordion id={'manageIntroPageAccordion'} defaultActiveKey={['1']}>
        <SimpleAccordion.Panel
          header="Manage Quick-Start Intro"
          eventKey="1"
          noCollapse
        >
          <PanelSpecialIndent>
            <UniversalLoader
              show={isLoading}
              content="Loading intro content..."
            />
            <JoditEditor
              ref={editor}
              value={validatedData?.data?.intro || ''}
              config={config}
              onChange={() => {}}
            />
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <StandardButton
                onClick={saveContent}
                disabled={updateLearningMutation.isPending}
              >
                {updateLearningMutation.isPending
                  ? 'Saving...'
                  : 'Save Content'}
              </StandardButton>
            </div>
          </PanelSpecialIndent>
        </SimpleAccordion.Panel>
      </SimpleAccordion>
    </>
  )
}

export default ManageIntroLearningPage
